import React, { useContext, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Space, Spin, message } from 'antd';
import { AuthContext } from '../../../context/AuthContext';
import api from '../../../authentication/axiosInstance';

export default function MassMeetingButtons() {
  const { userRole } = useContext(AuthContext) || {};
  const [kind, setKind] = useState(null);
  const [preview, setPreview] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState([]);
  const [step, setStep] = useState('select');
  const [search, setSearch] = useState('');
  const busy = useRef(false);
  const generation = useRef(0);
  const availableStaff = preview?.staff || [];

  const open = async (value) => {
    const current = ++generation.current;
    setKind(value);
    setPreview(null);
    setSelected([]);
    setStep('select');
    setSearch('');
    setText('');
    setError('');
    try {
      const response = await api.get('/dispatch/meeting-test/');
      if (generation.current === current) setPreview(response.data);
    } catch (err) {
      if (generation.current === current) setError('Could not load the recipient count. Close and try again.');
    }
  };
  const close = () => {
    if (busy.current) return;
    generation.current += 1;
    setKind(null);
  };
  const send = async () => {
    if (busy.current || !preview || !selected.length) return;
    busy.current = true;
    setSending(true);
    setError('');
    try {
      const response = await api.post('/dispatch/meeting-test/', {
        request_id: preview.request_id, kind, custom_message: text, staff_ids: selected,
      });
      message.success(`${response.data.recipient_count} staff inbox messages created. Phone alerts are being attempted.`);
      setKind(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not confirm the send. Retry here to avoid sending duplicates.');
    } finally {
      busy.current = false;
      setSending(false);
    }
  };

  if (userRole !== 'admin') return null;
  return <>
    <Space>
      <Button onClick={() => open('warning')} disabled={Boolean(kind)}>Mass warning</Button>
      <Button onClick={() => open('Loader')} disabled={Boolean(kind)}>Mass dispatch</Button>
    </Space>
    <Modal title={step === 'select' ? 'Select field staff' : (kind === 'warning' ? 'Confirm Mass Warning' : 'Confirm Mass Dispatch')}
      open={Boolean(kind)} onCancel={close} onOk={step === 'select' ? () => setStep('confirm') : send} confirmLoading={sending}
      okText={step === 'select' ? `Continue with ${selected.length} selected` : `Send to ${selected.length} selected staff`} cancelText="Cancel" closable={!sending}
      maskClosable={!sending} keyboard={!sending} cancelButtonProps={{ disabled: sending }}
      okButtonProps={{ disabled: !preview || !selected.length }}>
      <Alert type="warning" showIcon message="MEETING TEST — no work assignment" />
      {!preview && !error && <Spin aria-label="Loading recipients" />}
      {preview && <>
        {step === 'select' ? <>
          <p>{availableStaff.length} eligible staff. {selected.length} selected.</p>
          <Space style={{ marginBottom: 12 }}>
            <Button onClick={() => setSelected(availableStaff.map(member => member.id))}>Select all</Button>
            <Button onClick={() => setSelected([])}>Clear selection</Button>
          </Space>
          <Input aria-label="Search staff" placeholder="Search name, phone or email" value={search} onChange={event => setSearch(event.target.value)} />
          <div style={{ maxHeight: 340, overflowY: 'auto', marginTop: 12 }}>
            {availableStaff.filter(member => `${member.name} ${member.phone || ''} ${member.email || ''}`.toLowerCase().includes(search.toLowerCase())).map(member =>
              <div key={member.id} style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <Checkbox checked={selected.includes(member.id)} onChange={event => setSelected(previous => event.target.checked ? [...previous, member.id] : previous.filter(id => id !== member.id))}>
                  {member.name} (#{member.id}) — {member.phone || member.email || 'No contact details'}{member.phone_registered ? ' · Phone registered' : ''}
                </Checkbox>
              </div>)}
          </div>
        </> : <>
        <p><strong>{selected.length} selected staff</strong> will receive this {kind === 'warning' ? 'warning' : 'Loader dispatch'}. No assigned route is required.</p>
        <p>{availableStaff.filter(member => selected.includes(member.id) && member.phone_registered).length} of {selected.length} selected staff have a registered phone. Push: {preview.push_enabled ? 'configured' : 'not configured'}. Email: {preview.email_enabled ? 'enabled' : 'off'}.</p>
        <p>Staff should tap <strong>{kind === 'warning' ? "I'm available" : 'Confirm'}</strong>. No storm or dispatch assignment is needed. This does not send WhatsApp messages.</p>
        <label htmlFor="mass-meeting-message">Custom message (optional)</label>
        <Input.TextArea id="mass-meeting-message" value={text} onChange={e => setText(e.target.value)} maxLength={300} showCount disabled={sending} />
        <Button onClick={() => setStep('select')} disabled={sending} style={{ marginTop: 12 }}>Change selected staff</Button>
        </>}
      </>}
      {error && <Alert type="error" role="alert" message={error} style={{ marginTop: 16 }} />}
    </Modal>
  </>;
}
